/**
 * SettingsTab: 설정 다이얼로그 > 설정 탭
 * v1 원본: RenewalComponents/Footer/SettingsDialog/SettingsTab
 * Redux connect → useAppSelector/useAppDispatch 전환
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { uiActions } from '@/store/slices/uiSlice';
import { captionActions } from '@/store/slices/captionSlice';
import { voiceNoteActions } from '@/store/slices/voiceNoteSlice';
import {
  translationActions,
  TRANSLATION_LANGUAGES,
  type TranslationLanguage,
} from '@/store/slices/translationSlice';
import { getConferenceManager } from '@/conference/conferenceManagerRef';
import { CONFERENCE_COMMANDS } from '@/conference/commands';

const SettingsTab: React.FC = () => {
  const dispatch = useAppDispatch();
  const maxGridSize = useAppSelector((s) => s.ui.maxGridSize);
  const isSpeechEnabled = useAppSelector((s) => s.caption.isEnabled);
  const translation = useAppSelector((s) => s.translation);
  const local = useAppSelector((s) => s.local);

  const [isOpenLanguageDropdown, setIsOpenLanguageDropdown] = useState(false);
  const languageDropdownRef = useRef<HTMLDivElement>(null);

  const gridOptions = [6, 9, 12, 16];

  // 드롭다운 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (languageDropdownRef.current && !languageDropdownRef.current.contains(e.target as Node)) {
        setIsOpenLanguageDropdown(false);
      }
    };
    if (isOpenLanguageDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpenLanguageDropdown]);

  const handleSelectLanguage = useCallback(
    (code: TranslationLanguage) => {
      dispatch(translationActions.setLanguage(code));
      setIsOpenLanguageDropdown(false);

      const mgr = getConferenceManager();

      mgr?.sendCommand(CONFERENCE_COMMANDS.WEHAGO_ID, {
        value: local.jitsiId,
        attributes: {
          nationality: code,
        },
      });

      if (translation.isTranslateEnabled) {
        mgr?.sendCommand(CONFERENCE_COMMANDS.TRANSLATION_TOGGLE_STATUS, {
          attributes: {
            userId: local.jitsiId,
            enabled: 'true',
            language: code,
          },
        });
      }
    },
    [
      dispatch,
      local.jitsiId,
      translation.isTranslateEnabled,
    ]
  );

  const handleChangeGridMax = useCallback(
    (count: number) => {
      dispatch(uiActions.setMaxGridSize(count));
    },
    [dispatch]
  );

  const currentLangLabel =
    TRANSLATION_LANGUAGES.find((l) => l.code === translation.language)?.label ?? '한국어';

  return (
    <>
      <dl className="rtcDialogSetupNew__info">
        <div>
          <dt className="textTop">비디오</dt>
          <dd>
            <p className="infoText">
              그리드 뷰에서 한 화면에 표시할 최대 사용자 수를 선택하세요
            </p>
            <div className="rtcDialogSetupNew__infoSetgroup1">
              {gridOptions.map((num) => (
                <div key={num} className="common_radio_input type_blue2 lhFix">
                  <label>
                    <input
                      className="radio_input"
                      type="radio"
                      name="gridMaxUserCount"
                      checked={maxGridSize === num}
                      onChange={() => handleChangeGridMax(num)}
                    />
                    <span className="icon" />
                    <span className="label_text">{num}명</span>
                  </label>
                </div>
              ))}
            </div>
          </dd>
        </div>
        <div className="lineBar">
          <dt className="textTop">음성인식</dt>
          <dd>
            <div className="rtcDialogSetupNew__infoSetgroup2">
              <p className="infoText">
                사용자의 음성을 텍스트로 변환하여 자막과 번역을 제공하고, 회의
                <br />
                기록을 확인할 수 있습니다.
              </p>
              <div className="fixItem">
                <div className="common_onoff_switch small2 type_blue2">
                  <label>
                    <input
                      className="onoff_input"
                      type="checkbox"
                      checked={isSpeechEnabled}
                      onChange={(e) => {
                        dispatch(captionActions.setEnabled(e.target.checked));
                        dispatch(voiceNoteActions.setEnabled(e.target.checked));
                      }}
                    />
                    <span className="switch_img" />
                    <span className="label_text on" />
                    <span className="label_text off" />
                  </label>
                </div>
              </div>
            </div>
          </dd>
        </div>
        <div>
          <dt className="textTop">자막/번역 언어</dt>
          <dd className="flexEnd">
            <div
              className={`rtc_newDropdown${isOpenLanguageDropdown ? ' open' : ''}`}
              ref={languageDropdownRef}
            >
              <button
                type="button"
                className="rtc_newDropdown__btn"
                onClick={() => setIsOpenLanguageDropdown((prev) => !prev)}
              >
                <span>{currentLangLabel}</span>
                <span className="lunacon lunacon--arrowDown-outlined" />
              </button>
              {isOpenLanguageDropdown && (
                <ul className="rtc_newDropdown_list">
                  {TRANSLATION_LANGUAGES.map((lang) => (
                    <li key={lang.code}>
                      <button
                        type="button"
                        className={`rtc_newDropdown_listBtn ${translation.language === lang.code ? 'selected' : ''}`}
                        onClick={() => handleSelectLanguage(lang.code)}
                      >
                        {lang.label}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </dd>
        </div>
      </dl>
      <div className="rtcDialogSetupNew__infoTextBox">
        <p>
          *화상회의 중 음성인식은 실시간 분석 및 대화기록에만 사용되며 회의 종료
          후 음성 데이터는 저장되지 않습니다.
        </p>
      </div>
    </>
  );
};

export default SettingsTab;
